import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Grid, Button, TextField, MenuItem,
  Table, TableHead, TableRow, TableCell, TableBody, Chip, Alert,
  Divider, CircularProgress
} from '@mui/material';
import { ArrowBack, Print, Article } from '@mui/icons-material';
import axios from 'axios';
import CompanyHeader from './CompanyHeader';
import { locale } from '../utils/currency';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const fmt  = (d) => d
  ? new Date(d).toLocaleDateString(locale(), { day: '2-digit', month: 'short', year: 'numeric' })
  : '—';
const fmtDays = (n) => n > 0 ? `+${n}` : String(n);

const ENTRY_COLOR = {
  'Allocation':       'success',
  'Leave Taken':      'error',
  'Adjustment':       'warning',
  'Carry Forward':    'info',
  'Leave Recall':     'primary',
  'Forfeiture':       'default',
  'Leave Encashment': 'secondary',
};

const LeaveStatementPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [employees,  setEmployees]  = useState([]);
  const [filterEmp,  setFilterEmp]  = useState(searchParams.get('employeeNo') || '');
  const [filterYear, setFilterYear] = useState(new Date().getFullYear().toString());
  const [entries,    setEntries]    = useState([]);
  const [balances,   setBalances]   = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');

  useEffect(() => {
    axios.get(`${API}/api/employees`, { headers: hdrs() })
      .then(r => setEmployees(r.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!filterEmp) return;
    setLoading(true); setError('');
    try {
      const [entryRes, balRes] = await Promise.all([
        axios.get(`${API}/api/leave/ledger?employeeNo=${filterEmp}&year=${filterYear}`, { headers: hdrs() }),
        axios.get(`${API}/api/leave/balance/${filterEmp}?year=${filterYear}`, { headers: hdrs() }),
      ]);
      const sorted = (entryRes.data || []).sort((a, b) =>
        new Date(a.postingDate) - new Date(b.postingDate) ||
        a.leaveTypeCode.localeCompare(b.leaveTypeCode)
      );
      setEntries(sorted);
      setBalances(balRes.data || []);
    } catch { setError('Failed to load statement'); }
    finally { setLoading(false); }
  }, [filterEmp, filterYear]);

  useEffect(() => { load(); }, [load]);

  // Compute running balance per leave type as we iterate
  const entriesWithRunning = useMemo(() => {
    const run = {};
    return entries.map(e => {
      run[e.leaveTypeCode] = (run[e.leaveTypeCode] || 0) + e.days;
      return { ...e, running: run[e.leaveTypeCode] };
    });
  }, [entries]);

  const employee = employees.find(e => e.employeeNo === filterEmp);
  const years = [];
  for (let y = new Date().getFullYear() + 1; y >= 2022; y--) years.push(y.toString());

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4, mb: 6 }}>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
        }
      `}</style>

      {/* ── Header ── */}
      <Card elevation={3} className="no-print"
        sx={{ mb: 2, background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)', color: '#fff' }}>
        <CardContent sx={{ py: 2 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Button startIcon={<ArrowBack />} size="small" variant="outlined"
                onClick={() => navigate('/hr/leave')}
                sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)', '&:hover': { borderColor: '#fff' } }}>
                Back
              </Button>
              <Article />
              <Typography variant="h6" fontWeight={700}>Leave Statement</Typography>
            </Box>
            <Button variant="contained" startIcon={<Print />}
              onClick={() => window.print()}
              disabled={!filterEmp || entries.length === 0}
              sx={{ bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
              Print / Save PDF
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* ── Filters ── */}
      <Card elevation={2} className="no-print" sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1.5 }}>
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <TextField select label="Employee" value={filterEmp} size="small" sx={{ minWidth: 280 }}
              onChange={e => setFilterEmp(e.target.value)}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true, MenuProps: { PaperProps: { sx: { maxHeight: 300 } } } }}>
              <MenuItem value=""><em>— Select employee —</em></MenuItem>
              {employees.map(e => (
                <MenuItem key={e.employeeNo} value={e.employeeNo}>
                  {e.firstName} {e.lastName}
                  <span style={{ color: '#999', fontSize: 11, marginLeft: 6 }}>({e.employeeNo})</span>
                </MenuItem>
              ))}
            </TextField>
            <TextField select label="Year" value={filterYear} size="small" sx={{ minWidth: 110 }}
              onChange={e => setFilterYear(e.target.value)}
              InputLabelProps={{ shrink: true }}>
              {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </TextField>
            {loading && <CircularProgress size={20} />}
          </Box>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }} className="no-print">{error}</Alert>}

      {!filterEmp && (
        <Card elevation={1}>
          <CardContent>
            <Typography align="center" color="text.secondary" py={4}>
              Select an employee to generate their leave statement.
            </Typography>
          </CardContent>
        </Card>
      )}

      {filterEmp && !loading && entries.length === 0 && (
        <Card elevation={1}>
          <CardContent>
            <Typography align="center" color="text.secondary" py={4}>
              No ledger entries found for {filterYear}. Run leave allocation first.
            </Typography>
          </CardContent>
        </Card>
      )}

      {filterEmp && entries.length > 0 && (
        <Card elevation={3}>
          <CardContent>

            <CompanyHeader title="Leave Statement" subtitle={String(filterYear)} />

            {/* ── Employee block ── */}
            <Box mb={2.5} pb={1.5} sx={{ borderBottom: '2px solid #1565c0' }}>
              {employee && (
                <Grid container spacing={1} mt={0.5}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2">
                      <strong>Employee:</strong> {employee.firstName} {employee.lastName}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Emp No:</strong> {employee.employeeNo}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2">
                      <strong>Department:</strong> {employee.department || '—'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Job Title:</strong> {employee.jobTitle || '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2">
                      <strong>Status:</strong> {employee.status || '—'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Printed:</strong> {new Date().toLocaleDateString(locale())}
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </Box>

            {/* ── Balance Summary ── */}
            <Typography variant="subtitle2" fontWeight={700} mb={1}
              color="text.secondary" textTransform="uppercase" letterSpacing={0.8}>
              Balance Summary
            </Typography>
            <Table size="small" sx={{ mb: 3 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#e3f2fd' }}>
                  <TableCell><strong>Leave Type</strong></TableCell>
                  <TableCell align="right"><strong>Entitlement</strong></TableCell>
                  <TableCell align="right"><strong>Taken</strong></TableCell>
                  <TableCell align="right"><strong>Recalled</strong></TableCell>
                  <TableCell align="right"><strong>Adjustments</strong></TableCell>
                  <TableCell align="right"><strong>Balance</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {balances.map(b => {
                  const te          = entries.filter(e => e.leaveTypeCode === b.leaveTypeCode);
                  const entitlement = te.filter(e => ['Allocation', 'Carry Forward'].includes(e.entryType)).reduce((s, e) => s + e.days, 0);
                  const taken       = te.filter(e => e.entryType === 'Leave Taken').reduce((s, e) => s + Math.abs(e.days), 0);
                  const recall      = te.filter(e => e.entryType === 'Leave Recall').reduce((s, e) => s + e.days, 0);
                  const adj         = te.filter(e => e.entryType === 'Adjustment').reduce((s, e) => s + e.days, 0);
                  const balColor    = b.balance <= 0 ? 'error' : b.balance <= 5 ? 'warning' : 'success';
                  return (
                    <TableRow key={b.leaveTypeCode} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{b.leaveTypeName}</Typography>
                        <Typography variant="caption" fontFamily="monospace" color="text.secondary">
                          {b.leaveTypeCode}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{entitlement}</TableCell>
                      <TableCell align="right" sx={{ color: taken > 0 ? '#c62828' : 'inherit' }}>
                        {taken > 0 ? `−${taken}` : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ color: recall > 0 ? '#1565c0' : 'inherit' }}>
                        {recall > 0 ? `+${recall}` : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ color: adj !== 0 ? '#e65100' : 'inherit' }}>
                        {adj !== 0 ? fmtDays(adj) : '—'}
                      </TableCell>
                      <TableCell align="right">
                        <Chip label={`${b.balance} days`} size="small" color={balColor}
                          sx={{ fontWeight: 700, minWidth: 72 }} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            <Divider sx={{ my: 2 }} />

            {/* ── Transaction Detail ── */}
            <Typography variant="subtitle2" fontWeight={700} mb={1}
              color="text.secondary" textTransform="uppercase" letterSpacing={0.8}>
              Transaction Detail
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>Entry Type</strong></TableCell>
                  <TableCell><strong>Leave Type</strong></TableCell>
                  <TableCell align="right"><strong>Days</strong></TableCell>
                  <TableCell align="right"><strong>Running Bal</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell><strong>Ref No</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entriesWithRunning.map((e, i) => (
                  <TableRow key={i} hover sx={{ '&:nth-of-type(odd)': { bgcolor: 'grey.50' } }}>
                    <TableCell>
                      <Typography variant="caption" whiteSpace="nowrap">{fmt(e.postingDate)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={e.entryType} size="small"
                        color={ENTRY_COLOR[e.entryType] || 'default'} variant="outlined"
                        sx={{ fontSize: 11 }} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{e.leaveTypeName}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={700}
                        sx={{ color: e.days > 0 ? '#2e7d32' : '#c62828' }}>
                        {fmtDays(e.days)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={700}
                        sx={{ color: e.running < 0 ? '#c62828' : 'inherit' }}>
                        {e.running}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">{e.description || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" fontFamily="monospace">{e.applicationNo || '—'}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default LeaveStatementPage;
